/**
 * MeetingRoom.js — Phase 2A
 *
 * LiveKit-powered interactive video meeting room.
 * Replaces the old JitsiMeetExternalAPI iframe-based implementation.
 *
 * Features:
 *  - Pre-join screen: camera/mic preview + device selection + mic meter
 *  - Video grid: 1 / 2 / 2×2 / 3×3 responsive layout
 *  - Active speaker detection (glowing border)
 *  - Screen share (hero + filmstrip layout)
 *  - Chat (LiveKit data channel)
 *  - Participants panel with host mute capability
 *  - Recording (host only — red blinking indicator)
 *  - Auto-reconnect on unexpected disconnect
 *  - Leave / End (host)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Room,
  RoomEvent,
  VideoPresets,
  Track,
  createLocalVideoTrack,
  createLocalAudioTrack,
  LocalParticipant,
  DisconnectReason,
} from 'livekit-client';
import API from '../../Network/API';
import ShareMeeting from '../Modal/ShareMeeting';
import { toast } from 'react-toastify';
import './VideoMeeting.css';

// ─── Constants ───────────────────────────────────────────────────────────────

const CHAT_TOPIC   = 'meeting-chat';
const MAX_RECONNECT = 3;
const RECONNECT_DELAY_MS = 2500;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitial(name = '') {
  return (name.trim()[0] || '?').toUpperCase();
}

function getAvatar(participant) {
  if (!participant?.metadata) return null;
  try {
    const meta = JSON.parse(participant.metadata);
    return meta.avatar || null;
  } catch (e) {
    return null;
  }
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function gridClass(count) {
  if (count <= 1) return 'vm-grid--1';
  if (count === 2) return 'vm-grid--2';
  if (count <= 4) return 'vm-grid--4';
  if (count <= 9) return 'vm-grid--9';
  return 'vm-grid--12';
}

// ─── VideoTrack sub-component ────────────────────────────────────────────────

// objectFit defaults to 'cover' for camera tiles (fills tile, crops to fit).
// Pass objectFit='contain' for screen share so the full screen is never cropped.
function VideoTrackRenderer({ track, isLocal, objectFit = 'cover' }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !track) return;
    track.attach(el);
    return () => { track.detach(el); };
  }, [track]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted={isLocal}
      playsInline
      style={{ width: '100%', height: '100%', objectFit }}
    />
  );
}

function AudioTrackRenderer({ track }) {
  const audioRef = useRef(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !track) return;
    track.attach(el);
    return () => { track.detach(el); };
  }, [track]);

  return <audio ref={audioRef} autoPlay style={{ display: 'none' }} />;
}

// ─── Tile sub-component ──────────────────────────────────────────────────────

function ParticipantTile({
  participant,
  isLocal = false,
  isHost = false,
  small = false,
  onMute,
  canMuteOthers = false,
  reaction = null,
  handRaised = false,
  updateTrigger = 0,
}) {
  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTracks, setAudioTracks] = useState([]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(participant.isSpeaking);

  useEffect(() => {
    if (!participant) return;

    const updateTracks = () => {
      // Find camera track
      let camTrack = null;
      participant.videoTrackPublications?.forEach((pub) => {
        if (pub.source === Track.Source.Camera && pub.track) {
          camTrack = pub.track;
        }
      });
      setVideoTrack(camTrack);
      setIsVideoEnabled(!!camTrack && !camTrack.isMuted);

      // Check mic & audio tracks
      let micMuted = true;
      const auds = [];
      participant.audioTrackPublications?.forEach((pub) => {
        if (pub.source === Track.Source.Microphone) {
          micMuted = pub.isMuted || !pub.track;
        }
        if (pub.track) {
          auds.push(pub.track);
        }
      });
      setIsMicMuted(micMuted);
      setAudioTracks(auds);
    };

    updateTracks();
    const handleSpeaking = (speaking) => setIsSpeaking(speaking);
    
    participant.on('trackPublished',   updateTracks);
    participant.on('trackUnpublished', updateTracks);
    participant.on('trackMuted',       updateTracks);
    participant.on('trackUnmuted',     updateTracks);
    participant.on('trackSubscribed',  updateTracks);
    participant.on('isSpeakingChanged', handleSpeaking);

    return () => {
      participant.off('trackPublished',   updateTracks);
      participant.off('trackUnpublished', updateTracks);
      participant.off('trackMuted',       updateTracks);
      participant.off('trackUnmuted',     updateTracks);
      participant.off('trackSubscribed',  updateTracks);
      participant.off('isSpeakingChanged', handleSpeaking);
    };
  }, [participant, updateTrigger]);

  if (!participant) return null;

  const name    = participant.name || participant.identity || 'Unknown';
  const initial = getInitial(name);
  const avatar  = getAvatar(participant);

  return (
    <div
      className={`vm-tile${isSpeaking ? ' vm-tile--speaking' : ''}${isLocal ? ' vm-tile--local' : ''}${small ? ' vm-filmstrip__tile' : ''}`}
    >
      {isVideoEnabled && videoTrack ? (
        <VideoTrackRenderer track={videoTrack} isLocal={isLocal} />
      ) : (
        <div className={`vm-tile__avatar${small ? '' : ' vm-tile__avatar--lg'}`}>
          {avatar ? <img src={avatar} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} alt={name} /> : initial}
        </div>
      )}

      {/* Attach remote audio tracks quietly to the DOM */}
      {!isLocal && audioTracks.map((t) => (
        <AudioTrackRenderer key={t.sid} track={t} />
      ))}

      {/* Floating emoji reaction */}
      {reaction && (
        <div key={reaction.key} className="vm-tile__reaction">{reaction.emoji}</div>
      )}

      {/* Hand raised badge */}
      {handRaised && (
        <div className="vm-tile__hand-badge" title="Hand raised">✋</div>
      )}

      <div className="vm-tile__label">
        <span className="vm-tile__name" title={name}>{name}</span>
        <span className="vm-tile__badges">
          {isLocal && <span className="vm-tile__badge vm-tile__badge--you">You</span>}
          {isHost && <span className="vm-tile__badge vm-tile__badge--host">👑</span>}
          {isMicMuted && <span className="vm-tile__badge vm-tile__badge--muted">🔇</span>}
          {canMuteOthers && !isLocal && (
            <button
              className="vm-participant-action"
              title="Mute participant"
              onClick={() => onMute && onMute(participant.identity)}
              style={{ marginLeft: 4 }}
            >🔇</button>
          )}
        </span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MeetingRoom() {
  const { room: roomName } = useParams();
  const navigate           = useNavigate();
  const roomRef            = useRef(null);
  const isHostRef          = useRef(false); // always-current mirror of isHost state
  const encoder            = useRef(new TextEncoder());
  const decoder            = useRef(new TextDecoder());

  // ── State ──────────────────────────────────────────────────────────────────
  const [phase, setPhase]                   = useState('loading'); // loading | prejoin | waiting | room | ended
  const [roomTitle, setRoomTitle]           = useState('');
  const [isHost, setIsHost]                 = useState(false);
  const [joinData, setJoinData]             = useState(null);       // { token, livekit_url }

  // Keep isHostRef in sync so joinRoom's stale closure can read the current value
  useEffect(() => { isHostRef.current = isHost; }, [isHost]);

  // Pre-join
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);

  // Bug fix: Mirror state to mutable refs so the unmount closure can cleanly .stop() them.
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  useEffect(() => { localVideoTrackRef.current = localVideoTrack; }, [localVideoTrack]);
  useEffect(() => { localAudioTrackRef.current = localAudioTrack; }, [localAudioTrack]);

  const [camEnabled,    setCamEnabled]        = useState(true); // default ON — UX default aligns with initPreJoin
  const [micEnabled,    setMicEnabled]        = useState(true); // default ON — UX default aligns with initPreJoin
  const [cameras,       setCameras]           = useState([]);
  const [microphones,   setMicrophones]       = useState([]);
  const [selectedCam,   setSelectedCam]       = useState('');
  const [selectedMic,   setSelectedMic]       = useState('');
  const [micLevel,      setMicLevel]          = useState(0);
  const previewVideoRef = useRef(null);

  // Room
  const [participants, setParticipants]       = useState([]); // all including local
  const [updateTrigger, setUpdateTrigger]     = useState(0);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [screenShareTrack, setScreenShareTrack] = useState(null);
  const [screenShareOwner, setScreenShareOwner] = useState('');
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Controls
  const [camOn,   setCamOn]     = useState(true);
  const [micOn,   setMicOn]     = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // 'chat' | 'people' | null
  const chatOpen  = activePanel === 'chat';
  const panelOpen = activePanel === 'people';

  // Mobile: collapse secondary controls into a "More" (⋯) popup so the bar fits
  // without horizontal scrolling. Desktop keeps every button inline.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [reactionPopoverOpen, setReactionPopoverOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => { setIsMobile(e.matches); if (!e.matches) setMoreMenuOpen(false); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput,    setChatInput]    = useState('');
  const chatEndRef = useRef(null);

  // Timer
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  // Reconnect
  const reconnectAttemptsRef = useRef(0);
  const [reconnecting, setReconnecting] = useState(false);

  // ── Phase R1: Reactions + Hand Raise ──────────────────────────────────────
  const [reactions, setReactions]         = useState({}); // { identity: { emoji, key } }
  const [handRaisedIds, setHandRaisedIds] = useState(new Set()); // Set of participant identities
  const [handRaised, setHandRaised]       = useState(false); // local user's own hand state

  // Phase 2.5(b): Advanced Host Controls
  const [coHostIdentities, setCoHostIdentities] = useState(new Set());
  const [hostIdentity,     setHostIdentity]     = useState(''); // identity of the room host (visible to ALL clients)
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // tracking open 3-dot dropdowns
  const [unmuteRequestSender, setUnmuteRequestSender] = useState(null); // identity asking you to unmute
  const [isKicked, setIsKicked] = useState(false);
  const [isMuteAllActive, setIsMuteAllActive] = useState(false); // host toggle state
  const [isLockedMuted, setIsLockedMuted] = useState(false);    // participant lock state

  // Phase R3: Waitlist
  const [waitlist, setWaitlist] = useState([]);
  const [isWaitingRoomEnabled, setIsWaitingRoomEnabled] = useState(false);
  const waitlistCount = waitlist.length;

  const storedUserData = JSON.parse(localStorage.getItem('user_data') || '{}');
  const myIdentity     = String(storedUserData?.id || '');
  const myName         = storedUserData?.name || 'You';

  // ── Fetch join data ────────────────────────────────────────────────────────
  useEffect(() => {
    // Check for creator token stored by Meeting.js instant meeting flow
    const sessionKey = `livekit_meeting_${roomName}`;
    const cached     = sessionStorage.getItem(sessionKey);

    if (cached) {
      const data = JSON.parse(cached);
      sessionStorage.removeItem(sessionKey);
      setJoinData(data);
      setIsHost(true);
      // Host identity comes from cached data (set by Meeting.js at creation time)
      if (data.host_identity) setHostIdentity(String(data.host_identity));
      setRoomTitle(data.room_title || 'Meeting');
      // Restore waiting room state that was saved at meeting creation
      setIsWaitingRoomEnabled(data.is_waiting_room_enabled || false);
      initPreJoin();
      setPhase('prejoin');
      return;
    }

    // Otherwise call join API
    API.post(`/v1/meetings/${roomName}/join`)
      .then((res) => {
        const d = res.data;
        if (d.status === 'waiting') {
          setRoomTitle(d.room_title || 'Meeting');
          setPhase('waiting');
          return;
        }
        if (d.status === 'lobby') {
          setRoomTitle(d.room_title || 'Meeting');
          setPhase('lobby');
          return;
        }
        if (d.status === 'denied') {
          alert(d.message || 'The host has declined your request to join.');
          navigate('/meeting');
          return;
        }
        setJoinData({ token: d.token, livekit_url: d.livekit_url });
        setIsHost(d.is_host);
        // Populate host identity so every client's People panel knows who the host is
        if (d.host_identity) setHostIdentity(String(d.host_identity));
        setIsWaitingRoomEnabled(d.is_waiting_room_enabled || false);
        setRoomTitle(d.room_title || 'Meeting');
        initPreJoin();
        setPhase('prejoin');
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) {
          alert('Meeting not found.');
        } else {
          alert('Failed to join meeting. Please try again.');
        }
        navigate('/meeting');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);

  // Auto-poll when waiting for host to start
  useEffect(() => {
    if (phase !== 'waiting' && phase !== 'lobby') return;
    const id = setInterval(() => {
      API.post(`/v1/meetings/${roomName}/join`).then((res) => {
        const d = res.data;
        if (d.status === 'denied') {
          clearInterval(id);
          alert(d.message || "The host has declined your request to join.");
          navigate('/meeting');
          return;
        }
        if (d.status === 'joined') {
          clearInterval(id);
          setJoinData({ token: d.token, livekit_url: d.livekit_url });
          setIsHost(d.is_host);
          if (d.host_identity) setHostIdentity(String(d.host_identity));
          setIsWaitingRoomEnabled(d.is_waiting_room_enabled || false);
          initPreJoin();
          setPhase('prejoin');
        }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Fetch waitlist logic inside meeting
  useEffect(() => {
    if (!isHost || phase !== 'room') return;
    const fetchWaitlist = async () => {
      try {
        const res = await API.get(`/v1/meetings/${roomName}/waitlist`);
        if (res.data?.success) {
          const newList = res.data.data;
          setWaitlist((prevList) => {
            // Find anyone in the new list who wasn't in the previous list
            const prevIds = new Set(prevList.map(u => u.id));
            const newUsers = newList.filter(u => !prevIds.has(u.id));
            
            if (newUsers.length === 1) {
              toast.info(`☕ ${newUsers[0].name} entered the waiting room. Check the People tab.`);
            } else if (newUsers.length > 1) {
              toast.info(`☕ ${newUsers.length} new people entered the waiting room.`);
            }

            if (JSON.stringify(prevList) === JSON.stringify(newList)) {
              return prevList;
            }
            return newList;
          });
        }
      } catch (err) {}
    };
    fetchWaitlist();
    const id = setInterval(fetchWaitlist, 5000);
    return () => clearInterval(id);
  }, [isHost, phase, roomName]);

  // ── Pre-join setup ─────────────────────────────────────────────────────────
  async function initPreJoin() {
    try {
      // BUG FIX: Prevent Ghost Tracks from React Strict Mode racing
      localVideoTrackRef.current?.stop();
      localAudioTrackRef.current?.stop();

      const devices  = await navigator.mediaDevices.enumerateDevices();
      const cams     = devices.filter((d) => d.kind === 'videoinput');
      const mics     = devices.filter((d) => d.kind === 'audioinput');
      setCameras(cams);
      setMicrophones(mics);
      if (cams[0])  setSelectedCam(cams[0].deviceId);
      if (mics[0])  setSelectedMic(mics[0].deviceId);

      let vidTrack = null;
      if (cams.length > 0) {
        try {
          vidTrack = await createLocalVideoTrack({
            deviceId: cams[0].deviceId,
            resolution: VideoPresets.h720,
          });
          setLocalVideoTrack(vidTrack);
          if (previewVideoRef.current) vidTrack.attach(previewVideoRef.current);
        } catch (e) {
          console.warn('Camera init failed:', e);
          setCamEnabled(false);
        }
      } else {
        setCamEnabled(false);
      }

      let audTrack = null;
      if (mics.length > 0) {
        try {
          audTrack = await createLocalAudioTrack({
            deviceId: mics[0].deviceId,
          });
          setLocalAudioTrack(audTrack);
          startMicMeter(audTrack);
        } catch (e) {
          console.warn('Mic init failed:', e);
          setMicEnabled(false);
        }
      } else {
        setMicEnabled(false);
      }
    } catch (err) {
      console.warn('Pre-join device setup failed:', err);
    }
  }

  // Enforce attachment of pre-join local video to solve race conditions on mount/hot-reload
  useEffect(() => {
    if (previewVideoRef.current && localVideoTrack && camEnabled && phase === 'prejoin') {
      localVideoTrack.attach(previewVideoRef.current);
    }
  }, [localVideoTrack, camEnabled, phase]);

  function startMicMeter(track) {
    if (!track?.mediaStreamTrack) return;
    try {
      const ctx     = new AudioContext();
      const src     = ctx.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setMicLevel(Math.min(100, Math.round((avg / 128) * 100)));
        window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    } catch (_) {}
  }

  // Cleanup pre-join tracks when entering room or leaving
  function cleanupPreview() {
    // BUG FIX: Pierce the stale closure immediately natively bypassing React
    localVideoTrackRef.current?.stop();
    localAudioTrackRef.current?.stop();
    localVideoTrack?.stop();
    localAudioTrack?.stop();
  }

  // ── Join the LiveKit room ──────────────────────────────────────────────────
  const joinRoom = useCallback(async () => {
    if (!joinData) return;
    cleanupPreview();
    setPhase('room');
    startTimer();

    const room = new Room({
      adaptiveStream: true,
      dynacast:       true,
      videoCaptureDefaults: {
        resolution: VideoPresets.h720,
        deviceId:   selectedCam || undefined,
      },
      audioCaptureDefaults: {
        deviceId:        selectedMic || undefined,
        noiseSuppression: true,
        echoCancellation: true,
        autoGainControl:  true,
      },
      publishDefaults: {
        simulcast: true,
        dtx:       true,
      },
    });

    roomRef.current = room;

    // ── Room Events ─────────────────────────────────────────────────────────
    // Always use roomRef.current so refreshParticipants never reads a stale snapshot
    room.on(RoomEvent.ParticipantConnected,    () => refreshParticipants());
    room.on(RoomEvent.ParticipantDisconnected, (p) => {
      console.log('[Meeting] Participant left:', p.identity);
      refreshParticipants();
    });
    room.on(RoomEvent.LocalTrackPublished,     () => refreshParticipants());
    room.on(RoomEvent.LocalTrackUnpublished,   () => refreshParticipants());
    room.on(RoomEvent.TrackPublished,          () => refreshParticipants());
    room.on(RoomEvent.TrackUnpublished,        () => refreshParticipants());
    room.on(RoomEvent.TrackSubscribed,         (track) => handleTrackSubscribed(track));
    room.on(RoomEvent.TrackUnsubscribed,       (track) => handleTrackUnsubscribed(track));
    room.on(RoomEvent.DataReceived,            handleDataReceived);
    room.on(RoomEvent.Disconnected,            handleDisconnect);
    room.on(RoomEvent.Reconnecting,            () => setReconnecting(true));
    room.on(RoomEvent.Reconnected,             () => { setReconnecting(false); reconnectAttemptsRef.current = 0; });

    await room.connect(joinData.livekit_url, joinData.token);

    // ── BUG FIX: Read room metadata to detect active mute_all lock ─────────
    // mute_all is a one-time broadcast; new joiners must read room metadata
    // to know they should enter muted.
    const meta = room.metadata ? (() => { try { return JSON.parse(room.metadata); } catch { return {}; } })() : {};
    const shouldEnterMuted = meta.mute_all_active === true && !isHost;
    
    // Sync Room UI state dynamically with the user's explicit Pre-join selections!
    setCamOn(camEnabled);
    if (shouldEnterMuted) {
      setIsLockedMuted(true);
      setMicOn(false);
      toast.warning('You joined while the host has muted everyone', { autoClose: 4000 });
    } else {
      setMicOn(micEnabled);
    }

    // BUG FIX: Safari Camera Leak & Render Delay
    // Explicitly release the pre-join hardware locks before LiveKit requests them blindly
    if (localVideoTrackRef.current) {
      localVideoTrackRef.current.stop();
    }
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.stop();
    }
    if (localVideoTrack) {
      localVideoTrack.stop();
      setLocalVideoTrack(null);
    }
    if (localAudioTrack) {
      localAudioTrack.stop();
      setLocalAudioTrack(null);
    }

    // Publish local tracks — respect mute-all lock
    if (camEnabled && cameras.length > 0 && !shouldEnterMuted) {
      try {
        await room.localParticipant.setCameraEnabled(true);
      } catch (e) {
        console.warn('Failed to publish camera:', e);
        setCamEnabled(false);
        setCamOn(false);
      }
    }
    if (micEnabled && microphones.length > 0 && !shouldEnterMuted) {
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (e) {
        console.warn('Failed to publish mic:', e);
        setMicEnabled(false);
        setMicOn(false);
      }
    }

    setLocalParticipant(room.localParticipant);
    refreshParticipants(room);

    // ── Broadcast host identity so all current clients show the HOST badge ──
    // Use isHostRef.current — NOT isHost state — because joinRoom is a useCallback
    // and isHost is NOT in its dependency array (adding it would force room reconnection).
    // isHostRef is kept in sync with isHost via a dedicated useEffect.
    if (isHostRef.current) {
      const myId = String(storedUserData?.id || '');
      if (myId) {
        setHostIdentity(myId);
        try {
          const hostMsg = encoder.current.encode(JSON.stringify({ type: 'host_info', host_identity: myId }));
          room.localParticipant.publishData(hostMsg, { reliable: true });
        } catch (e) {
          console.warn('[Meeting] host_info broadcast failed:', e);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinData, camEnabled, micEnabled, selectedCam, selectedMic]);

  // Always reads from the live ref — avoids stale closure ghost participants
  function refreshParticipants() {
    const room = roomRef.current;
    if (!room) return;
    const remote = Array.from(room.remoteParticipants.values());
    setParticipants([room.localParticipant, ...remote].filter(Boolean));
    setUpdateTrigger(prev => prev + 1);
  }

  function handleTrackSubscribed(track) {
    const room = roomRef.current;
    if (!room) return;
    if (track.source === Track.Source.ScreenShare) {
      setScreenShareTrack(track);
      room.remoteParticipants.forEach((p) => {
        p.videoTrackPublications.forEach((pub) => {
          if (pub.source === Track.Source.ScreenShare) {
            setScreenShareOwner(p.name || p.identity);
          }
        });
      });
    }
    refreshParticipants();
  }

  function handleTrackUnsubscribed(track) {
    if (track.source === Track.Source.ScreenShare) {
      setScreenShareTrack(null);
      setScreenShareOwner('');
    }
  }

  function handleDataReceived(payload, participant) {
    try {
      const msg = JSON.parse(decoder.current.decode(payload));

      if (msg.type === CHAT_TOPIC) {
        setChatMessages((prev) => [...prev, {
          id:      Date.now(),
          sender:  msg.sender,
          text:    msg.text,
          isLocal: (participant?.identity === myIdentity) || msg.isLocal,
          time:    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }]);
        return;
      }

      // ── Phase R1 data channel messages ─────────────────────────────────────
      if (msg.type === 'reaction' && msg.emoji) {
        const identity = participant?.identity || msg.identity || '';
        if (!identity) return;
        const key = Date.now() + Math.random();
        setReactions((prev) => ({ ...prev, [identity]: { emoji: msg.emoji, key } }));
        setTimeout(() => {
          setReactions((prev) => {
            // Only clear if it's still the same reaction (avoid clearing a newer one)
            if (prev[identity]?.key === key) {
              const next = { ...prev };
              delete next[identity];
              return next;
            }
            return prev;
          });
        }, 2500);
        return;
      }

      if (msg.type === 'hand_raise') {
        const identity = participant?.identity || msg.identity || '';
        if (identity) setHandRaisedIds((prev) => new Set([...prev, identity]));
        return;
      }

      if (msg.type === 'hand_lower') {
        const identity = participant?.identity || msg.identity || '';
        if (identity) setHandRaisedIds((prev) => { const n = new Set(prev); n.delete(identity); return n; });
        return;
      }

      // ── Phase 2.5(b) data channel messages ──────────────────────────────────
      if (msg.type === 'kicked') {
        if (msg.identity === myIdentity) {
          setIsKicked(true);
          roomRef.current?.disconnect();
        }
        return;
      }
      
      if (msg.type === 'unmute_request') {
        if (msg.identity === myIdentity) {
          setUnmuteRequestSender(msg.from || 'Host');
        }
        return;
      }

      if (msg.type === 'host_info') {
        // Broadcast from the host (web or Flutter) on connect so all clients
        // know who the host is — enables HOST badge for everyone, not just the host.
        if (msg.host_identity) setHostIdentity(String(msg.host_identity));
        return;
      }

      if (msg.type === 'role_update') {
        if (msg.role === 'co_host') {
          setCoHostIdentities(prev => new Set([...prev, msg.identity]));
        } else {
          setCoHostIdentities(prev => { const n = new Set(prev); n.delete(msg.identity); return n; });
        }
        return;
      }

      if (msg.type === 'mute_all') {
        // Only applies to non-hosts / non-co-hosts
        const isCurrentUserHost = isHost;
        const isCurrentUserCoHost = coHostIdentities.has(myIdentity);
        if (!isCurrentUserHost && !isCurrentUserCoHost) {
          setIsLockedMuted(true);
          setMicOn(false);
          if (roomRef.current?.localParticipant) {
            roomRef.current.localParticipant.setMicrophoneEnabled(false).catch(() => {});
          }
          toast.warning('The host has muted everyone', { autoClose: 3000 });
        }
        return;
      }

      if (msg.type === 'unmute_all') {
        const isCurrentUserHost = isHost;
        const isCurrentUserCoHost = coHostIdentities.has(myIdentity);
        if (!isCurrentUserHost && !isCurrentUserCoHost) {
          setIsLockedMuted(false);
          toast.info('You can now unmute yourself', { autoClose: 3000 });
        }
        return;
      }

    } catch (_) {}
  }

  async function handleDisconnect(reason) {
    console.log('[Meeting] Disconnected, reason:', reason);
    // LiveKit DisconnectReason enum: clientInitiated = 1, roomDeleted/serverShutdown triggers cleanup too
    const cleanReasons = [
      DisconnectReason.CLIENT_INITIATED,
      DisconnectReason.ROOM_DELETED,
      DisconnectReason.SERVER_SHUTDOWN,
    ];
    if (cleanReasons.includes(reason)) {
      forceHardwareDeath();
      setPhase('ended');
      stopTimer();
      return;
    }
    // Unexpected disconnect — try to reconnect
    if (reconnectAttemptsRef.current < MAX_RECONNECT) {
      reconnectAttemptsRef.current += 1;
      setReconnecting(true);
      await new Promise((r) => setTimeout(r, RECONNECT_DELAY_MS));
      try {
        await roomRef.current.connect(joinData.livekit_url, joinData.token);
        setReconnecting(false);
      } catch (_) {
        if (reconnectAttemptsRef.current >= MAX_RECONNECT) {
          forceHardwareDeath();
          setPhase('ended');
          stopTimer();
        }
      }
    } else {
      forceHardwareDeath();
      setPhase('ended');
      stopTimer();
    }
  }

  // (The rogue duplicate unmount hook from Phase 1 was intentionally deleted here to prevent bifurcation)

  // ── Phase R1: Browser online/offline detection ────────────────────────────
  useEffect(() => {
    const handleOffline = () => {
      console.log('[Meeting] Browser went offline');
      setReconnecting(true);
    };
    const handleOnline = () => {
      console.log('[Meeting] Browser came online');
      // LiveKit Reconnected event will clear setReconnecting(false)
      // But if we're not in room phase, just clear the flag
      if (roomRef.current?.state !== 'connected') {
        setReconnecting(false);
      }
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online',  handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online',  handleOnline);
    };
  }, []);

  // ── Timer ──────────────────────────────────────────────────────────────────
  function startTimer() {
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  }
  function stopTimer() {
    clearInterval(timerRef.current);
  }

  // ── Phase R1: Send Reaction ─────────────────────────────────────────────────
  function sendReaction(emoji) {
    const room = roomRef.current;
    if (!room) return;
    const identity = room.localParticipant.identity;
    // Broadcast to all remote participants
    const payload = encoder.current.encode(JSON.stringify({
      type: 'reaction', emoji, identity,
    }));
    room.localParticipant.publishData(payload, { reliable: false });
    // Show locally on our own tile immediately
    const key = Date.now() + Math.random();
    setReactions((prev) => ({ ...prev, [identity]: { emoji, key } }));
    setTimeout(() => {
      setReactions((prev) => {
        if (prev[identity]?.key === key) {
          const next = { ...prev }; delete next[identity]; return next;
        }
        return prev;
      });
    }, 2500);
  }

  // ── Phase R1: Toggle Hand Raise ─────────────────────────────────────────────
  function toggleHandRaise() {
    const room = roomRef.current;
    if (!room) return;
    const identity = room.localParticipant.identity;
    const newVal = !handRaised;
    setHandRaised(newVal);
    // Update local set so our own tile shows the badge
    setHandRaisedIds((prev) => {
      const next = new Set(prev);
      if (newVal) next.add(identity); else next.delete(identity);
      return next;
    });
    // Broadcast to all participants
    const payload = encoder.current.encode(JSON.stringify({
      type: newVal ? 'hand_raise' : 'hand_lower', identity,
    }));
    room.localParticipant.publishData(payload, { reliable: true });
  }

  // ── Controls ───────────────────────────────────────────────────────────────

  async function toggleMic() {
    // Blocked while host has locked all mics
    if (isLockedMuted) {
      toast.warning('The host has muted everyone. You cannot unmute.', { autoClose: 2000 });
      return;
    }
    const room = roomRef.current;
    if (!room) return;
    const enabled = !micOn;
    try {
      await room.localParticipant.setMicrophoneEnabled(enabled);
      setMicOn(enabled);
    } catch (e) {
      console.warn('Mic toggle failed:', e);
      setMicOn(false);
      alert('Could not start microphone. Please check permissions or hardware.');
    }
  }

  async function toggleCam() {
    const room = roomRef.current;
    if (!room) return;
    const enabled = !camOn;
    try {
      await room.localParticipant.setCameraEnabled(enabled);
      setCamOn(enabled);
      refreshParticipants(room);
    } catch (e) {
      console.warn('Camera toggle failed:', e);
      setCamOn(false);
      alert('Could not start camera. Please check permissions or hardware.');
    }
  }


  async function toggleScreenShare() {
    const room = roomRef.current;
    if (!room) return;
    if (isSharingScreen) {
      await room.localParticipant.setScreenShareEnabled(false);
      setIsSharingScreen(false);
      setScreenShareTrack(null);
      setScreenShareOwner('');
    } else {
      try {
        await room.localParticipant.setScreenShareEnabled(true);
        setIsSharingScreen(true);
        setScreenShareOwner(myName);
        // Grab the screen share track for hero display
        room.localParticipant.videoTrackPublications.forEach((pub) => {
          if (pub.source === Track.Source.ScreenShare && pub.track) {
            setScreenShareTrack(pub.track);
          }
        });
      } catch (err) {
        if (err.name !== 'NotAllowedError') {
          console.warn('Screen share error:', err);
        }
      }
    }
  }

  function sendChatMessage() {
    if (!chatInput.trim() || !roomRef.current) return;
    const msg = {
      type:    CHAT_TOPIC,
      sender:  myName,
      text:    chatInput.trim(),
      isLocal: true,
    };
    const payload = encoder.current.encode(JSON.stringify(msg));
    roomRef.current.localParticipant.publishData(payload, { reliable: true });
    setChatMessages((prev) => [...prev, {
      id:      Date.now(),
      sender:  myName,
      text:    msg.text,
      isLocal: true,
      time:    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
    setChatInput('');
  }

  async function muteParticipant(identity) {
    if (!isHost && !coHostIdentities.has(myIdentity)) return;
    try {
      await API.post(`/v1/meetings/${roomName}/participants/${identity}/mute`);
      toast.info('Participant muted', { autoClose: 2000 });
    } catch (_) {}
  }

  async function requestUnmute(identity) {
    if (!isHost && !coHostIdentities.has(myIdentity)) return;
    const msg = { type: 'unmute_request', identity, from: myName };
    const payload = encoder.current.encode(JSON.stringify(msg));
    roomRef.current.localParticipant.publishData(payload, { reliable: true });
    toast.info('Unmute request sent', { autoClose: 2000 });
  }

  async function kickParticipant(identity) {
    if (!isHost && !coHostIdentities.has(myIdentity)) return;
    // Send kicked message first so their client disconnects and redirects gracefully
    const msg = { type: 'kicked', identity };
    const payload = encoder.current.encode(JSON.stringify(msg));
    roomRef.current.localParticipant.publishData(payload, { reliable: true });

    try {
      await API.delete(`/v1/meetings/${roomName}/participants/${identity}`);
      toast.info('Participant removed', { autoClose: 2000 });
    } catch (_) {}
  }

  async function muteAll() {
    if (!isHost && !coHostIdentities.has(myIdentity)) return;
    if (!isMuteAllActive) {
      // Activate mute lock — send data message first so all clients lock immediately
      const msg = { type: 'mute_all' };
      const payload = encoder.current.encode(JSON.stringify(msg));
      roomRef.current.localParticipant.publishData(payload, { reliable: true });
      setIsMuteAllActive(true);
      try {
        // Also mute via backend LiveKit API (handles stubborn clients)
        await API.post(`/v1/meetings/${roomName}/mute-all`, { host_identity: myIdentity });
        // Persist to room metadata so new joiners enter muted
        await API.post(`/v1/meetings/${roomName}/metadata`, { metadata: JSON.stringify({ mute_all_active: true }) });
        toast.info('All participants muted', { autoClose: 2000 });
      } catch (_) {}
    } else {
      // Deactivate mute lock — send unmute_all so clients can unmute themselves
      const msg = { type: 'unmute_all' };
      const payload = encoder.current.encode(JSON.stringify(msg));
      roomRef.current.localParticipant.publishData(payload, { reliable: true });
      setIsMuteAllActive(false);
      try {
        await API.post(`/v1/meetings/${roomName}/metadata`, { metadata: JSON.stringify({ mute_all_active: false }) });
      } catch (_) {}
      toast.success('Participants can now unmute themselves', { autoClose: 2000 });
    }
  }

  async function promoteToCoHost(identity) {
    if (!isHost && !coHostIdentities.has(myIdentity)) return;
    setCoHostIdentities(prev => new Set([...prev, identity]));
    const msg = { type: 'role_update', identity, role: 'co_host' };
    const payload = encoder.current.encode(JSON.stringify(msg));
    roomRef.current.localParticipant.publishData(payload, { reliable: true });
    toast.success('Promoted to Co-Host', { autoClose: 2000 });
  }

  async function demoteCoHost(identity) {
    if (!isHost && !coHostIdentities.has(myIdentity)) return;
    setCoHostIdentities(prev => { const n = new Set(prev); n.delete(identity); return n; });
    const msg = { type: 'role_update', identity, role: 'participant' };
    const payload = encoder.current.encode(JSON.stringify(msg));
    roomRef.current.localParticipant.publishData(payload, { reliable: true });
    toast.info('Removed from Co-Host', { autoClose: 2000 });
  }

  // Bug fix: Explicitly force hardware destruction before socket close
  function forceHardwareDeath() {
    const lp = roomRef.current?.localParticipant;
    if (lp) {
      // 1. Tell LiveKit Device Manager to officially return WebRTC OS locks natively.
      lp.setCameraEnabled(false).catch(() => {});
      lp.setMicrophoneEnabled(false).catch(() => {});
      lp.setScreenShareEnabled(false).catch(() => {});
      
      // 2. Bruteforce stop any physically remaining tracks tracked by the LocalParticipant instance
      lp.videoTrackPublications?.forEach((pub) => {
        if (pub.track) pub.track.stop();
      });
      lp.audioTrackPublications?.forEach((pub) => {
        if (pub.track) pub.track.stop();
      });
    }
  }

  async function performLeave() {
    forceHardwareDeath();
    await roomRef.current?.disconnect();
    setPhase('ended');
    stopTimer();
  }

  async function performEnd() {
    try {
      await API.post(`/v1/meetings/${roomName}/end`);
    } catch (_) {}
    forceHardwareDeath();
    await roomRef.current?.disconnect();
    setPhase('ended');
    stopTimer();
  }

  function handleConfirmAction() {
    if (confirmAction === 'leave') performLeave();
    else if (confirmAction === 'end') performEnd();
    setConfirmAction(null);
  }

  async function toggleRecording() {
    try {
      if (isRecording) {
        await API.post(`/v1/meetings/${roomName}/recording/stop`);
        setIsRecording(false);
      } else {
        const res = await API.post(`/v1/meetings/${roomName}/recording/start`);
        if (res.data.success) setIsRecording(true);
      }
    } catch (_) {}
  }

  // ── Waitlist Logic ─────────────────────────────────────────────────────────

  const handleWaitlistAction = async (userId, action) => {
    try {
      await API.post(`/v1/meetings/${roomName}/waitlist/action`, {
        user_id: userId,
        action: action
      });
      // Optimistic update
      setWaitlist(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleWaitingRoom = async () => {
    try {
      const newState = !isWaitingRoomEnabled;
      await API.post(`/v1/meetings/${roomName}/toggle-waiting-room`, { enabled: newState });
      setIsWaitingRoomEnabled(newState);
    } catch (err) {
      console.error(err);
    }
  };

  // ── Scroll chat to bottom ──────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // ── Unified Cleanup on unmount ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopTimer();
      // Bug fix: Pierce the stale closure by accessing the mutable Refs
      localVideoTrackRef.current?.stop();
      localAudioTrackRef.current?.stop();
      
      // Bruteforce kill all LiveKit managed OS hardware locks instantly regardless of exit vector
      forceHardwareDeath();
      
      const room = roomRef.current;
      if (room && room.state !== 'disconnected') {
        console.log('[Meeting] Unified Unmount, disconnecting room fully');
        room.disconnect();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Phase 2.5(b) Computed sorted list ──────────────────────────────────────

  // Identity is now 'userId_nonce' (multi-device format) or just 'userId' (legacy).
  // Use this helper everywhere instead of strict equality so the same user
  // on web + iOS + Android all get the HOST badge correctly.
  function identityMatchesUserId(identity, userId) {
    if (!identity || !userId) return false;
    const s = String(userId);
    return identity === s || identity.startsWith(s + '_');
  }

  const sortedParticipants = [...participants].sort((a, b) => {
    const effectiveHost = hostIdentity || (isHost ? myIdentity : '');
    const isHostA = identityMatchesUserId(a.identity, effectiveHost);
    const isHostB = identityMatchesUserId(b.identity, effectiveHost);
    if (isHostA && !isHostB) return -1;
    if (!isHostA && isHostB) return 1;

    const isCoHostA = coHostIdentities.has(a.identity);
    const isCoHostB = coHostIdentities.has(b.identity);
    if (isCoHostA && !isCoHostB) return -1;
    if (!isCoHostA && isCoHostB) return 1;

    const nameA = (a.name || a.identity).toLowerCase();
    const nameB = (b.name || b.identity).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Helper: true if a given participant is the host (works for ALL clients)
  function isParticipantHost(p) {
    const effectiveHost = hostIdentity || (isHost ? myIdentity : '');
    return identityMatchesUserId(p.identity, effectiveHost);
  }

  // ─── Render phases ─────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="vm-root">
        <div className="vm-waiting">
          <div className="vm-waiting__spinner" />
          <div className="vm-waiting__title">Connecting…</div>
        </div>
      </div>
    );
  }

  if (phase === 'waiting') {
    return (
      <div className="vm-root">
        <div className="vm-waiting">
          <div className="vm-waiting__spinner" />
          <div className="vm-waiting__title">{roomTitle || 'Meeting'}</div>
          <div className="vm-waiting__sub">Waiting for the host to start the meeting…</div>
          <button
            className="vm-btn--leave"
            style={{ marginTop: 24, padding: '10px 24px', borderRadius: 10,
                     background: 'transparent', border: '1px solid rgba(218,54,51,0.4)',
                     color: '#e6edf3', cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => navigate('/meeting')}
          >← Leave</button>
        </div>
      </div>
    );
  }

  if (phase === 'lobby') {
    return (
      <div className="vm-root">
        <div className="vm-waiting" style={{ animation: 'pulse 2s infinite' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>☕</div>
          <div className="vm-waiting__title">You're in the waiting room</div>
          <div className="vm-waiting__sub">The host will let you in soon. Hang tight!</div>
          <button
            className="vm-btn--leave"
            style={{ marginTop: 32, padding: '10px 32px', borderRadius: 8,
                     background: 'var(--vm-danger)', border: 'none',
                     color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
            onClick={() => navigate('/meeting')}
          >Leave</button>
        </div>
      </div>
    );
  }

  if (phase === 'ended') {
    return (
      <div className="vm-root">
        <div className="vm-waiting">
          <div style={{ fontSize: 40 }}>👋</div>
          <div className="vm-waiting__title">Meeting ended</div>
          <div className="vm-waiting__sub">{formatTime(elapsed)} — Thanks for joining!</div>
          <button
            style={{ marginTop: 24, padding: '10px 24px', borderRadius: 10,
                     background: '#2f81f7', border: 'none',
                     color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
                     fontSize: 14, fontWeight: 600 }}
            onClick={() => navigate('/meeting')}
          >Back to Meetings</button>
        </div>
      </div>
    );
  }

  if (phase === 'prejoin') {
    return (
      <div className="vm-root">
        <div className="vm-prejoin">
          <div className="vm-prejoin__card">

            {/* Camera preview */}
            <div className="vm-prejoin__preview">
              {camEnabled ? (
                <video
                  ref={previewVideoRef}
                  autoPlay muted playsInline
                  className="vm-prejoin__video"
                />
              ) : (
                <div className="vm-prejoin__avatar-placeholder">
                  <div className="icon">👤</div>
                  <span style={{ fontSize: 13 }}>Camera is off</span>
                </div>
              )}

              <div className="vm-prejoin__preview-controls">
                <button
                  className={`vm-icon-btn${!micEnabled ? ' vm-icon-btn--muted' : ''}`}
                  onClick={() => {
                    const next = !micEnabled;
                    setMicEnabled(next);
                    if (localAudioTrack) {
                      localAudioTrack.isMuted = !next;
                    }
                  }}
                  title={micEnabled ? 'Mute mic' : 'Unmute mic'}
                >
                  {micEnabled ? '🎤' : '🔇'}
                </button>
                <button
                  className={`vm-icon-btn${!camEnabled ? ' vm-icon-btn--muted' : ''}`}
                  onClick={async () => {
                    const next = !camEnabled;
                    if (!next) {
                      setCamEnabled(false);
                      localVideoTrackRef.current?.stop();
                      localVideoTrack?.stop();
                      setLocalVideoTrack(null);
                    } else {
                      try {
                        localVideoTrackRef.current?.stop(); // Bug fix: slaughter any ghost tracks before fetching new ones
                        const t = await createLocalVideoTrack({ deviceId: selectedCam });
                        setLocalVideoTrack(t);
                        setCamEnabled(true);
                        if (previewVideoRef.current) t.attach(previewVideoRef.current);
                      } catch (e) {
                        alert('Could not start camera. Please check permissions or hardware.');
                      }
                    }
                  }}
                  title={camEnabled ? 'Turn off camera' : 'Turn on camera'}
                >
                  {camEnabled ? '📹' : '📷'}
                </button>
              </div>
            </div>

            {/* Side panel */}
            <div className="vm-prejoin__side">
              <h2 className="vm-prejoin__title">{roomTitle || 'Join Meeting'}</h2>
              <p className="vm-prejoin__subtitle">Room: <code style={{ fontSize: 11 }}>{roomName}</code></p>

              {cameras.length > 1 && (
                <>
                  <div className="vm-prejoin__label">Camera</div>
                  <select
                    className="vm-prejoin__select"
                    value={selectedCam}
                    onChange={(e) => setSelectedCam(e.target.value)}
                  >
                    {cameras.map((c) => (
                      <option key={c.deviceId} value={c.deviceId}>{c.label || 'Camera'}</option>
                    ))}
                  </select>
                </>
              )}

              {microphones.length > 0 && (
                <>
                  <div className="vm-prejoin__label">Microphone</div>
                  <select
                    className="vm-prejoin__select"
                    value={selectedMic}
                    onChange={(e) => setSelectedMic(e.target.value)}
                  >
                    {microphones.map((m) => (
                      <option key={m.deviceId} value={m.deviceId}>{m.label || 'Microphone'}</option>
                    ))}
                  </select>
                </>
              )}

              <div className="vm-prejoin__label">Mic level</div>
              <div className="vm-prejoin__mic-meter">
                <div className="vm-prejoin__mic-meter-fill" style={{ width: `${micLevel}%` }} />
              </div>

              <div className="vm-prejoin__spacer" />

              <button className="vm-prejoin__join-btn" onClick={joinRoom}>
                ▶ Join Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Room phase ─────────────────────────────────────────────────────────────
  const isScreenSharing = !!screenShareTrack;
  const remoteParticipants = participants.filter((p) => p !== localParticipant);

  // Determine which tiles to show in grid vs filmstrip
  const gridParticipants = isScreenSharing
    ? [] // filmstrip only when screen sharing
    : participants;

  return (
    <div className="vm-root">
      {/* Content */}
      <div className="vm-content">
        {/* Grid / Screen share wrapper */}
        <div className={`vm-grid-wrapper${isScreenSharing ? ' vm-grid-wrapper--screenshare' : ''}`}>

          {isScreenSharing ? (
            <>
              {/* Hero — screen share: use contain so the full screen is visible */}
              <div className="vm-screenshare-hero">
                <VideoTrackRenderer track={screenShareTrack} isLocal={isSharingScreen} objectFit="contain" />
                <div className="vm-screenshare-label">
                  🖥️ {screenShareOwner} is sharing
                </div>
              </div>

              {/* Filmstrip — participant thumbnails */}
              <div className="vm-filmstrip">
                {participants.map((p) => (
                  <ParticipantTile
                    key={p.identity}
                    participant={p}
                    isLocal={p === localParticipant}
                    isHost={isHost && p === localParticipant}
                    small
                    reaction={reactions[p.identity] || null}
                    handRaised={handRaisedIds.has(p.identity)}
                    updateTrigger={updateTrigger}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className={`vm-grid ${gridClass(participants.length)}`}>
              {participants.map((p) => (
                <ParticipantTile
                  key={p.identity}
                  participant={p}
                  isLocal={p === localParticipant}
                  isHost={isHost && p === localParticipant}
                  canMuteOthers={isHost}
                  onMute={muteParticipant}
                  reaction={reactions[p.identity] || null}
                  handRaised={handRaisedIds.has(p.identity)}
                  updateTrigger={updateTrigger}
                />
              ))}
            </div>
          )}
        </div>

        {/* Combined Chat + Participants Panel */}
        <div className={`vm-panel${activePanel ? ' vm-panel--open' : ''}`}>
          {/* Tab bar */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid var(--vm-border)',
            flexShrink: 0,
          }}>
            <button
              onClick={() => setActivePanel('chat')}
              style={{
                flex: 1, padding: '12px 8px', background: 'none', border: 'none',
                borderBottom: `2px solid ${activePanel === 'chat' ? 'var(--vm-accent)' : 'transparent'}`,
                color: activePanel === 'chat' ? 'var(--vm-accent)' : 'var(--vm-text-muted)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              💬 Chat
              {chatMessages.length > 0 && activePanel !== 'chat' && (
                <span style={{ background: 'var(--vm-accent)', color: '#fff', fontSize: 9, borderRadius: 8, padding: '1px 5px', fontWeight: 700 }}>
                  {chatMessages.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActivePanel('people')}
              style={{
                flex: 1, padding: '12px 8px', background: 'none', border: 'none',
                borderBottom: `2px solid ${activePanel === 'people' ? 'var(--vm-accent)' : 'transparent'}`,
                color: activePanel === 'people' ? 'var(--vm-accent)' : 'var(--vm-text-muted)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              👥 People ({participants.length})
            </button>
            <button
              className="vm-panel__close"
              onClick={() => setActivePanel(null)}
              style={{ margin: '8px 12px' }}
            >✕</button>
          </div>
          {/* Chat Tab Content */}
          {activePanel === 'chat' && (
            <>
              <div className="vm-chat">
                {chatMessages.length === 0 && (
                  <div style={{ color: 'var(--vm-text-muted)', textAlign: 'center', marginTop: 24, fontSize: 13 }}>
                    No messages yet. Say hello! 👋
                  </div>
                )}
                {chatMessages.map((msg) => (
                  <div key={msg.id} className={`vm-chat__msg${msg.isLocal ? ' vm-chat__msg--local' : ''}`}>
                    <div className="vm-chat__meta">
                      <span>{msg.sender}</span>
                      <span>{msg.time}</span>
                    </div>
                    <div className="vm-chat__bubble">{msg.text}</div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="vm-chat__input-area">
                <textarea
                  className="vm-chat__input"
                  placeholder="Type a message…"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                  rows={1}
                />
                <button className="vm-chat__send-btn" onClick={sendChatMessage}>➤</button>
              </div>
            </>
          )}

          {/* People Tab Content */}
          {activePanel === 'people' && (
            <div className="vm-participants" style={{ paddingTop: 8 }}>
              
              {/* Host Settings */}
              {isHost && (
                <div style={{ padding: '12px 16px', marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--vm-text)' }}>Waiting Room</div>
                    <div style={{ fontSize: 12, color: 'var(--vm-text-muted)', marginTop: 2 }}>Hold users before admitting</div>
                  </div>
                  <div className="form-check form-switch m-0 pb-1">
                    <input 
                      onChange={handleToggleWaitingRoom} 
                      type="checkbox" 
                      role="switch"
                      className="form-check-input m-0" 
                      checked={isWaitingRoomEnabled}
                      style={{ cursor: 'pointer', transform: 'scale(1.2)', backgroundColor: isWaitingRoomEnabled ? '#238636' : '' }}
                    />
                  </div>
                </div>
              )}

              {isHost && waitlistCount > 0 && (
                <div style={{ paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--vm-text-muted)', fontWeight: 600, marginBottom: 12, paddingLeft: 16 }}>
                    Waiting Room ({waitlistCount})
                  </div>
                  {waitlist.map((w) => (
                    <div key={w.id} className="vm-participant-row">
                      <div className="vm-participant-row__avatar" style={{ border: '2px solid var(--vm-warning)', color: 'var(--vm-text)' }}>
                        {w.avatar ? <img src={w.avatar} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} alt={w.name} /> : getInitial(w.name)}
                      </div>
                      <div className="vm-participant-row__info">
                        <div className="vm-participant-row__name">{w.name}</div>
                        <div className="vm-participant-row__role" style={{ color: 'var(--vm-warning)' }}>Waiting...</div>
                      </div>
                      <div className="vm-participant-row__actions" style={{ display: 'flex', gap: 6, opacity: 1, visibility: 'visible' }}>
                        <button style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6, background: '#238636', color: '#fff', border: '1px solid rgba(240,246,252,0.1)', cursor: 'pointer', fontWeight: 500 }} onClick={() => handleWaitlistAction(w.id, 'admitted')}>Admit</button>
                        <button style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6, background: 'transparent', color: '#f85149', border: '1px solid rgba(248,81,73,0.4)', cursor: 'pointer', fontWeight: 500 }} onClick={() => handleWaitlistAction(w.id, 'denied')}>Deny</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ padding: '0 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--vm-text-muted)', fontWeight: 600 }}>
                  In Meeting ({participants.length})
                </div>
                {(isHost || coHostIdentities.has(myIdentity)) && participants.length > 1 && (
                  <button
                    onClick={muteAll}
                    style={{
                      fontSize: 12,
                      color: isMuteAllActive ? '#22c55e' : 'var(--vm-danger)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{isMuteAllActive ? '🔓' : '🔇'}</span>
                    {isMuteAllActive ? 'Unlock Mics' : 'Mute All'}
                  </button>
                )}
              </div>
              
              {sortedParticipants.map((p) => {
                const isLocalP = p === localParticipant;
                const name     = p.name || p.identity;
                const avatar   = getAvatar(p);
                // Use hostIdentity (known by ALL clients from join API + host_info broadcast)
                // NOT isHost && isLocalP which only works on the host's own screen.
                const effectiveHostId = hostIdentity || (isHost ? myIdentity : '');
                const isUserHost = identityMatchesUserId(p.identity, effectiveHostId);
                const isUserCoHost = coHostIdentities.has(p.identity);
                const roleBadge = isUserHost ? 'HOST' : (isUserCoHost ? 'CO-HOST' : 'PARTICIPANT');
                const badgeColor = isUserHost ? '#2563EB' : (isUserCoHost ? '#6D28D9' : 'var(--vm-text-muted)');
                // Permission matrix:
                //   Host      → can manage ANY non-local participant
                //   Co-host   → can manage regular participants ONLY (not the host, not themselves)
                //   Participant → no controls
                const amICoHost = coHostIdentities.has(myIdentity);
                const canManage = !isLocalP && (
                  isHost
                    ? true                   // host manages everyone
                    : amICoHost && !isUserHost // co-host manages participants only
                );
                const menuOpen = actionMenuOpen === p.identity;

                // check if currently muted
                let isMuted = true;
                p.audioTrackPublications?.forEach(pub => {
                  if (pub.source === Track.Source.Microphone) isMuted = pub.isMuted || !pub.track;
                });

                return (
                  <div key={p.identity} className="vm-participant-row" style={{ position: 'relative' }}>
                    <div className="vm-participant-row__avatar">
                      {avatar ? <img src={avatar} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} alt={name} /> : getInitial(name)}
                    </div>
                    <div className="vm-participant-row__info">
                      <div className="vm-participant-row__name">
                        {name}{isLocalP ? ' (You)' : ''} {handRaisedIds.has(p.identity) && <span style={{ marginLeft: 6, fontSize: '14px' }} title="Hand raised">✋</span>}
                      </div>
                      <div className="vm-participant-row__role" style={{ marginTop: 2 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                          color: badgeColor, border: `1px solid ${badgeColor}`,
                          padding: '2px 6px', borderRadius: 4, background: `${badgeColor}15`
                        }}>
                          {roleBadge}
                        </span>
                      </div>
                    </div>
                    {canManage && (
                      <div className="vm-participant-row__actions" style={{ position: 'relative', overflow: 'visible' }}>
                        <button
                          className="vm-participant-action"
                          title="Manage"
                          onClick={() => setActionMenuOpen(menuOpen ? null : p.identity)}
                        >⋮</button>
                        
                        {menuOpen && (
                          <div style={{
                            position: 'absolute', right: 0, top: 30, zIndex: 1000,
                            background: '#1C2333', border: '1px solid var(--vm-border)',
                            borderRadius: 8, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', minWidth: 180, overflow: 'hidden'
                          }}>
                              {!isMuted ? (
                                <button className="vm-dropdown-btn" onClick={() => { muteParticipant(p.identity); setActionMenuOpen(null); }}>
                                  🔇 Mute
                                </button>
                              ) : (
                                <button className="vm-dropdown-btn" onClick={() => { requestUnmute(p.identity); setActionMenuOpen(null); }}>
                                  🔔 Request Unmute
                                </button>
                              )}
                              
                              {/* Make/Remove Co-Host — host only */}
                              {isHost && (
                                !isUserCoHost ? (
                                  <button className="vm-dropdown-btn" onClick={() => { promoteToCoHost(p.identity); setActionMenuOpen(null); }}>
                                    ⭐️ Make Co-Host
                                  </button>
                                ) : (
                                  <button className="vm-dropdown-btn" onClick={() => { demoteCoHost(p.identity); setActionMenuOpen(null); }}>
                                    ⭐️ Remove Co-Host
                                  </button>
                                )
                              )}
                              
                              {/* Remove — host only */}
                              {isHost && (
                                <button className="vm-dropdown-btn" style={{ color: 'var(--vm-danger)' }} onClick={() => {
                                  if(window.confirm(`Remove ${name} from this meeting?`)) kickParticipant(p.identity);
                                  setActionMenuOpen(null);
                                }}>
                                  🚫 Remove
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Reconnecting overlay */}
      {reconnecting && (
        <div className="vm-overlay">
          <div className="vm-overlay__icon">🔄</div>
          <div className="vm-overlay__title">Reconnecting…</div>
          <div className="vm-overlay__sub">Please wait</div>
        </div>
      )}

      {/* Confirm Dialog Overlay */}
      {confirmAction && (
        <div className="vm-overlay">
          <div className="vm-prejoin__card" style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 24px 80px rgba(0,0,0,0.8)', borderRadius: 16 }}>
            <div className="vm-panel__header" style={{ padding: '20px 24px', borderBottom: '1px solid var(--vm-border)' }}>
              <span className="vm-panel__title" style={{ fontSize: 18 }}>
                {confirmAction === 'leave' ? 'Leave Meeting' : 'End Meeting'}
              </span>
              <button className="vm-panel__close" onClick={() => setConfirmAction(null)}>✕</button>
            </div>
            <div style={{ padding: '24px', fontSize: 14, color: 'var(--vm-text-muted)', lineHeight: 1.5 }}>
              {confirmAction === 'leave' 
                ? 'Are you sure you want to leave this meeting?' 
                : 'Are you sure you want to end this meeting for everyone? All participants will be disconnected.'}
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--vm-border)', display: 'flex', gap: 12, justifyContent: 'flex-end', background: 'var(--vm-surface-2)', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <button 
                onClick={() => setConfirmAction(null)}
                style={{ padding: '10px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--vm-border)', color: 'var(--vm-text)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmAction}
                style={{ padding: '10px 16px', borderRadius: 8, background: 'var(--vm-danger)', border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
              >
                {confirmAction === 'leave' ? 'Leave' : 'End for Everyone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2.5(b) Kicked Alert Overlay */}
      {isKicked && (
        <div className="vm-overlay" style={{ zIndex: 99999 }}>
          <div className="vm-prejoin__card" style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 24px 80px rgba(0,0,0,0.8)', borderRadius: 16 }}>
            <div className="vm-panel__header" style={{ padding: '20px 24px', borderBottom: '1px solid var(--vm-border)' }}>
              <span className="vm-panel__title" style={{ fontSize: 18 }}>Removed</span>
            </div>
            <div style={{ padding: '24px', fontSize: 14, color: 'var(--vm-text-muted)', lineHeight: 1.5 }}>
              You have been removed from this meeting by the host.
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--vm-border)', display: 'flex', gap: 12, justifyContent: 'flex-end', background: 'var(--vm-surface-2)', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <button 
                onClick={() => navigate('/meeting')}
                style={{ padding: '10px 16px', borderRadius: 8, background: 'var(--vm-danger)', border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2.5(b) Unmute Request Overlay */}
      {unmuteRequestSender && (
        <div className="vm-overlay" style={{ zIndex: 99999 }}>
          <div className="vm-prejoin__card" style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 24px 80px rgba(0,0,0,0.8)', borderRadius: 16 }}>
            <div className="vm-panel__header" style={{ padding: '20px 24px', borderBottom: '1px solid var(--vm-border)' }}>
              <span className="vm-panel__title" style={{ fontSize: 18 }}>Unmute Request</span>
            </div>
            <div style={{ padding: '24px', fontSize: 14, color: 'var(--vm-text-muted)', lineHeight: 1.5 }}>
              {unmuteRequestSender} has requested that you unmute your microphone.
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--vm-border)', display: 'flex', gap: 12, justifyContent: 'flex-end', background: 'var(--vm-surface-2)', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <button 
                onClick={() => setUnmuteRequestSender(null)}
                style={{ padding: '10px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--vm-border)', color: 'var(--vm-text)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
              >
                Stay Muted
              </button>
              <button 
                onClick={() => {
                  setUnmuteRequestSender(null);
                  if(!micOn) toggleMic();
                }}
                style={{ padding: '10px 16px', borderRadius: 8, background: '#2563EB', border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
              >
                Unmute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase R1: Emoji Reaction Popover — opened via the React button (desktop)
          or the React entry in the More menu (mobile). No longer always visible. */}
      {reactionPopoverOpen && (
        <>
          <div
            onClick={() => setReactionPopoverOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 29 }}
          />
          <div className="vm-reaction-bar">
            {['👍', '🔥', '👏', '❤️', '😂', '😮'].map((emoji) => (
              <button
                key={emoji}
                className="vm-reaction-btn"
                onClick={() => { sendReaction(emoji); setReactionPopoverOpen(false); }}
                title={`React with ${emoji}`}
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Controls bar */}
      <div className="vm-controls">
        <div className="vm-controls__left">
          {/* Connects branding — Zoom-style: logo + wordmark bottom-left */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src="/assets/img/logo.png"
              alt="Connects"
              style={{ height: 22, opacity: 0.85, objectFit: 'contain' }}
            />
            <div style={{
              width: 1, height: 18,
              background: 'var(--vm-border)',
            }} />
            <span style={{ fontSize: 13, color: 'var(--vm-text-muted)', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              {isRecording && <span className="vm-header__recording-dot" title="Recording active" />}
              {formatTime(elapsed)}
            </span>
          </div>
        </div>

        {/* Mic */}
        <button
          className={`vm-btn${!micOn ? ' vm-btn--muted' : ''}`}
          onClick={toggleMic}
          id="btn-toggle-mic"
          aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'}
        >
          <span className="vm-btn__icon">{micOn ? '🎤' : '🔇'}</span>
          <span className="vm-btn__label">{micOn ? 'Mute' : 'Unmute'}</span>
        </button>

        {/* Camera */}
        <button
          className={`vm-btn${!camOn ? ' vm-btn--muted' : ''}`}
          onClick={toggleCam}
          id="btn-toggle-cam"
          aria-label={camOn ? 'Turn off camera' : 'Turn on camera'}
        >
          <span className="vm-btn__icon">
            {camOn ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="15" height="12" rx="2.5" />
                <path d="M17 9l5-2.5v9L17 13" />
                <circle cx="9.5" cy="12" r="2.5" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="2" y1="2" x2="22" y2="22" />
                <path d="M10.66 6H14a2 2 0 0 1 2 2v2.34" />
                <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2" />
                <path d="M22 8l-6 4 6 4V8z" />
              </svg>
            )}
          </span>
          <span className="vm-btn__label">{camOn ? 'Camera' : 'Off'}</span>
        </button>

        {/* Screen share — desktop inline; mobile lives in the More menu */}
        {!isMobile && (
          <button
            className={`vm-btn${isSharingScreen ? ' vm-btn--active' : ''}`}
            onClick={toggleScreenShare}
            id="btn-screen-share"
            aria-label={isSharingScreen ? 'Stop sharing' : 'Share screen'}
          >
            <span className="vm-btn__icon">🖥️</span>
            <span className="vm-btn__label">{isSharingScreen ? 'Stop' : 'Share'}</span>
          </button>
        )}

        {!isMobile && <div className="vm-controls__divider" />}

        {/* Chat */}
        <button
          className={`vm-btn${activePanel === 'chat' ? ' vm-btn--active' : ''}`}
          onClick={() => setActivePanel(activePanel === 'chat' ? null : 'chat')}
          id="btn-chat"
          aria-label="Toggle chat"
        >
          <span className="vm-btn__icon">💬</span>
          <span className="vm-btn__label">Chat</span>
          {chatMessages.length > 0 && activePanel !== 'chat' && (
            <span style={{
              position: 'absolute', top: 6, right: 6,
              width: 8, height: 8, borderRadius: '50%',
              background: 'var(--vm-accent)',
            }} />
          )}
        </button>

        {/* Participants */}
        <button
          className={`vm-btn${activePanel === 'people' ? ' vm-btn--active' : ''}`}
          onClick={() => setActivePanel(activePanel === 'people' ? null : 'people')}
          id="btn-participants"
          aria-label="Toggle participants list"
        >
          <span className="vm-btn__icon" style={{ position: 'relative' }}>
            👥
            <span style={{
              position: 'absolute', top: -4, right: -6,
              background: 'var(--vm-accent)', color: '#fff', fontSize: 9,
              borderRadius: '50%', padding: '2px 4px', fontWeight: 'bold'
            }}>
              {participants.length}
            </span>
          </span>
          <span className="vm-btn__label">People</span>
        </button>

        {/* React — desktop inline; mobile lives in the More menu */}
        {!isMobile && (
          <button
            className={`vm-btn${reactionPopoverOpen ? ' vm-btn--active' : ''}`}
            onClick={() => setReactionPopoverOpen((o) => !o)}
            id="btn-react"
            aria-label="Send a reaction"
            aria-haspopup="true"
            aria-expanded={reactionPopoverOpen}
          >
            <span className="vm-btn__icon">😀</span>
            <span className="vm-btn__label">React</span>
          </button>
        )}

        {/* Raise Hand — desktop inline; mobile lives in the More menu */}
        {!isMobile && (
          <button
            className={`vm-btn${handRaised ? ' vm-btn--active' : ''}`}
            onClick={toggleHandRaise}
            id="btn-raise-hand"
            aria-label={handRaised ? 'Lower hand' : 'Raise hand'}
          >
            <span className="vm-btn__icon">✋</span>
            <span className="vm-btn__label">{handRaised ? 'Lower' : 'Hand'}</span>
            {/* Badge for host: count of raised hands */}
            {isHost && handRaisedIds.size > 0 && !handRaised && (
              <span style={{
                position: 'absolute', top: 2, right: 2,
                background: '#f59e0b', color: '#000', fontSize: 9,
                borderRadius: '50%', width: 14, height: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold',
              }}>
                {handRaisedIds.size}
              </span>
            )}
          </button>
        )}

        {/* Invite — desktop inline; mobile lives in the More menu */}
        {!isMobile && (
          <button
            className="vm-btn"
            onClick={() => setShowInviteModal(true)}
            id="btn-invite"
            aria-label="Invite others"
          >
            <span className="vm-btn__icon">✉️</span>
            <span className="vm-btn__label">Invite</span>
          </button>
        )}

        {/* Recording — host only; desktop inline, mobile lives in the More menu */}
        {!isMobile && isHost && (
          <button
            className={`vm-btn${isRecording ? ' vm-btn--muted' : ''}`}
            onClick={toggleRecording}
            id="btn-record"
            aria-label={isRecording ? 'Stop recording' : 'Start recording'}
          >
            <span className="vm-btn__icon">{isRecording ? '⏹️' : '⏺️'}</span>
            <span className="vm-btn__label">{isRecording ? 'Stop' : 'Record'}</span>
          </button>
        )}

        {/* More (⋯) — mobile only: opens the popup rendered outside the bar */}
        {isMobile && (
          <button
            className={`vm-btn${moreMenuOpen ? ' vm-btn--active' : ''}`}
            onClick={() => setMoreMenuOpen((o) => !o)}
            id="btn-more"
            aria-label="More options"
            aria-haspopup="true"
            aria-expanded={moreMenuOpen}
          >
            <span className="vm-btn__icon">⋯</span>
            <span className="vm-btn__label">More</span>
            {isHost && handRaisedIds.size > 0 && (
              <span style={{
                position: 'absolute', top: 2, right: 2,
                background: '#f59e0b', color: '#000', fontSize: 9,
                borderRadius: '50%', width: 14, height: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold',
              }}>
                {handRaisedIds.size}
              </span>
            )}
          </button>
        )}

        {!isMobile && <div className="vm-controls__divider" />}

        {/* Host: only "End Meeting". Non-host: only "Leave". */}
        {isHost ? (
          <button className="vm-btn--end" onClick={() => setConfirmAction('end')} id="btn-end-meeting">
            <span style={{ marginRight: 6 }}>✕</span> End Meeting
          </button>
        ) : (
          <button className="vm-btn--leave" onClick={() => setConfirmAction('leave')} id="btn-leave">
            <span style={{ marginRight: 6 }}>🚪</span> Leave
          </button>
        )}
      </div>

      {/* Mobile "More" popup — rendered OUTSIDE .vm-controls so the bar's
          backdrop-filter/overflow can't trap or clip a position:fixed child. */}
      {isMobile && moreMenuOpen && (
        <>
          {/* Click-away backdrop */}
          <div
            onClick={() => setMoreMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 55 }}
          />
          <div className="vm-more-menu">
            <button
              className="vm-more-menu__item"
              onClick={() => { setMoreMenuOpen(false); setReactionPopoverOpen(true); }}
            >
              <span className="vm-more-menu__icon">😀</span>
              React
            </button>
            <button
              className={`vm-more-menu__item${isSharingScreen ? ' vm-more-menu__item--active' : ''}`}
              onClick={() => { toggleScreenShare(); setMoreMenuOpen(false); }}
            >
              <span className="vm-more-menu__icon">🖥️</span>
              {isSharingScreen ? 'Stop Sharing' : 'Share Screen'}
            </button>
            <button
              className={`vm-more-menu__item${handRaised ? ' vm-more-menu__item--active' : ''}`}
              onClick={() => { toggleHandRaise(); setMoreMenuOpen(false); }}
            >
              <span className="vm-more-menu__icon">✋</span>
              {handRaised ? 'Lower Hand' : 'Raise Hand'}
              {isHost && handRaisedIds.size > 0 && (
                <span className="vm-more-menu__count">{handRaisedIds.size}</span>
              )}
            </button>
            <button
              className="vm-more-menu__item"
              onClick={() => { setShowInviteModal(true); setMoreMenuOpen(false); }}
            >
              <span className="vm-more-menu__icon">✉️</span>
              Invite
            </button>
            {isHost && (
              <button
                className={`vm-more-menu__item${isRecording ? ' vm-more-menu__item--active' : ''}`}
                onClick={() => { toggleRecording(); setMoreMenuOpen(false); }}
              >
                <span className="vm-more-menu__icon">{isRecording ? '⏹️' : '⏺️'}</span>
                {isRecording ? 'Stop Recording' : 'Record'}
              </button>
            )}
          </div>
        </>
      )}

      {/* Share / Invite Modal */}
      {showInviteModal && (
        <ShareMeeting
          isModalVisible={showInviteModal}
          handleShowB={() => setShowInviteModal(false)}
          meetingRoom={{
            room_name: roomName,
            room_title: roomTitle || roomName,
            start_date: new Date().toISOString(),
            time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }}
        />
      )}
    </div>
  );
}